import { useEffect, useMemo, useRef } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import {
  chartPalette,
  computeWidgetView,
  dataAsOf,
  deriveBrandColors,
  formatDate,
  type DataRow,
  type Dataset,
  type DatasetSnapshotMeta,
  type ReportDefinition
} from '@tallyframe/core';
import { BrandLine, WidgetContent } from '@tallyframe/renderer';
import { useCardPayload } from '../api/public-queries';
import { ApiError } from '../api/errors';
import type { CardPayload } from '../api/types';

/** The card payload's fields, once `isCardPayload` has vouched for them. The
 *  client type is deliberately loose (`[key: string]: unknown`), so this route
 *  narrows it locally rather than trusting an unchecked cast. */
interface CardData {
  kind: 'card';
  workspaceName: string;
  definition: ReportDefinition;
  dataset: Dataset;
  snapshot: DatasetSnapshotMeta;
  rows: DataRow[];
  brand: { name: string; accent: string; logoUrl: string | null; badge: boolean };
}

/** Like the print route, this page runs unattended in headless Chromium: a
 *  payload it cannot render must fail through the explicit error flag, not
 *  throw mid-render and hang the screenshot until its timeout. */
function isCardPayload(payload: CardPayload): payload is CardPayload & CardData {
  const p = payload as Record<string, unknown>;
  const brand = p.brand as Record<string, unknown> | null | undefined;
  const definition = p.definition as { widgets?: unknown } | null | undefined;
  return (
    p.kind === 'card' &&
    typeof p.workspaceName === 'string' &&
    typeof definition === 'object' &&
    definition !== null &&
    Array.isArray(definition.widgets) &&
    typeof p.dataset === 'object' &&
    p.dataset !== null &&
    typeof p.snapshot === 'object' &&
    p.snapshot !== null &&
    Array.isArray(p.rows) &&
    typeof brand === 'object' &&
    brand !== null &&
    typeof brand.name === 'string' &&
    typeof brand.accent === 'string' &&
    (typeof brand.logoUrl === 'string' || brand.logoUrl === null) &&
    typeof brand.badge === 'boolean'
  );
}

function raiseReady(): void {
  document.body.dataset.printReady = 'true';
  window.__TALLYFRAME_PRINT_READY__ = true;
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => resolve());
    else setTimeout(resolve, 16);
  });
}

/**
 * The unfurl card: the page headless Chromium screenshots at 1200x630 for a
 * published link's preview image (spec section 3).
 *
 * Public and bare like the print route, and with the same readiness contract:
 * `__TALLYFRAME_PRINT_READY__` is raised once fonts are loaded, any `data:`
 * logo has decoded and the charts have had their frames to draw, and a failed
 * payload fetch raises `__TALLYFRAME_PRINT_ERROR__` and the ready flag so the
 * render fails fast instead of waiting out its timeout.
 */
export function CardRoute(props: { exportId: string }): ReactElement {
  const query = useCardPayload(props.exportId);
  const { data: result, error: failure } = query;
  const outcome = useMemo((): { payload: CardData | null; error: ApiError | null } => {
    if (failure) {
      return {
        payload: null,
        error: ApiError.isApiError(failure)
          ? failure
          : new ApiError('EXPORT_FAILED', failure instanceof Error ? failure.message : String(failure), 0)
      };
    }
    if (result && !isCardPayload(result)) {
      return {
        payload: null,
        error: new ApiError('EXPORT_FAILED', 'Card payload is missing fields or is not a card payload.', 0)
      };
    }
    return { payload: result ?? null, error: null };
  }, [result, failure]);
  const { payload, error } = outcome;

  useEffect(() => {
    if (!error) return;
    window.__TALLYFRAME_PRINT_ERROR__ = { code: error.code, message: error.message };
    document.body.dataset.printError = 'true';
    raiseReady();
  }, [error]);

  const rendered = useMemo(() => {
    if (!payload) return null;
    const widgets = payload.definition.widgets;
    const kpis = widgets.filter((w) => w.type === 'kpi').slice(0, 3);
    const chart = kpis.length === 0 ? widgets.find((w) => w.type === 'chart') : undefined;
    const data = { dataset: payload.dataset, snapshot: payload.snapshot, rows: payload.rows };
    const { mark, text } = deriveBrandColors(payload.brand.accent);
    return {
      kpiViews: kpis.map((w) => computeWidgetView(w, data)),
      chartView: chart ? computeWidgetView(chart, data) : undefined,
      brand: {
        name: payload.brand.name,
        logoUrl: payload.brand.logoUrl,
        mark,
        text,
        palette: chartPalette(mark)
      }
    };
  }, [payload]);

  const cardRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!payload) return undefined;
    let cancelled = false;
    void (async () => {
      const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
      // A font that never loads must not wedge the render: carry on either way.
      await (fonts?.ready ?? Promise.resolve()).catch(() => undefined);
      const logo = cardRef.current?.querySelector<HTMLImageElement>('img.tf-brand-logo');
      if (logo && payload.brand.logoUrl?.startsWith('data:') && typeof logo.decode === 'function') {
        await logo.decode().catch(() => undefined);
      }
      // Charts draw once fonts are ready (ChartCanvas) with animation off, so two
      // frames after that the canvas is painted.
      await nextFrame();
      await nextFrame();
      if (cancelled) return;
      raiseReady();
    })();
    return () => {
      cancelled = true;
    };
  }, [payload]);

  if (!payload || !rendered) return <div className="tf-card" />;

  const style = {
    width: 1200,
    height: 630,
    fontFamily: 'var(--rp-font)',
    '--rp-brand': rendered.brand.mark,
    '--rp-brand-text': rendered.brand.text
  } as CSSProperties;

  return (
    <article
      className="tf-card tf-root"
      style={style}
      data-testid="unfurl-card"
      ref={(node) => {
        cardRef.current = node;
      }}
    >
      <div className="tf-header-bar" />
      <BrandLine brand={rendered.brand} />
      <h1 className="tf-header-title">{payload.definition.title}</h1>
      <p className="tf-header-subtitle">{payload.workspaceName}</p>
      <p className="tf-header-meta">Data as of {formatAsOf(payload.snapshot)}</p>
      {rendered.kpiViews.length > 0 ? (
        <div className="tf-print-kpis">
          {rendered.kpiViews.map((view) => (
            <div className="tf-print-kpi" key={view.widget.id} data-testid="card-kpi">
              <WidgetContent view={view} animate={false} />
            </div>
          ))}
        </div>
      ) : null}
      {rendered.chartView ? (
        <div className="tf-print-block" data-testid="card-chart">
          <h2 className="tf-print-block-title">{rendered.chartView.widget.title}</h2>
          <WidgetContent view={rendered.chartView} animate={false} />
        </div>
      ) : null}
      {payload.brand.badge ? <p className="tf-card-badge">Made with ReportPad</p> : null}
    </article>
  );
}

function formatAsOf(snapshot: DatasetSnapshotMeta): string {
  return formatDate(dataAsOf(snapshot));
}
